import type { CardProps } from './Card';
import type { Agenda, Module } from 'types/modules';
import { resolveAgendaName } from 'utils/resolveAgendaName';

export const MODULE_WIDGET_MAX_SUBORDINATE_ITEMS = 5;
export const OVERVIEW_PINNED_MODULE_LIMIT = 4;
export const OVERVIEW_FLOW_AGENDA_LIMIT = 3;

type ModuleWidgetAgendaOptions = {
  pinnedOnly?: boolean;
  maxItems?: number;
};

export const mapAgendaToCardItem = (agenda: Agenda): Pick<CardProps, 'title' | 'agendaId' | 'sourceType' | 'isPinned'> => ({
  title: resolveAgendaName(agenda),
  agendaId: agenda.agendaId,
  sourceType: agenda.systemType,
  isPinned: agenda.isPinned,
});

export const filterVisibleAgendas = (agendas: Agenda[], { pinnedOnly = false }: ModuleWidgetAgendaOptions = {}) =>
  agendas.filter((agenda) => !agenda.hideInOverview && (!pinnedOnly || agenda.isPinned));

export const mapAgendasToCardItems = (
  agendas: Agenda[],
  { pinnedOnly = false, maxItems = MODULE_WIDGET_MAX_SUBORDINATE_ITEMS }: ModuleWidgetAgendaOptions = {}
) => filterVisibleAgendas(agendas, { pinnedOnly }).slice(0, maxItems).map(mapAgendaToCardItem);

export const getModuleCardItems = (module: Module, options?: ModuleWidgetAgendaOptions) => mapAgendasToCardItems(module.agendas, options);

export const getChildModulesWithAgendas = (module: Module, options?: ModuleWidgetAgendaOptions): Omit<Module, 'childs'>[] | undefined => {
  const { pinnedOnly = false, maxItems = MODULE_WIDGET_MAX_SUBORDINATE_ITEMS } = options ?? {};
  const children = module.childs?.filter((child) => child.agendas.some((agenda) => !agenda.hideInOverview && (!pinnedOnly || agenda.isPinned)));

  if (!children?.length) {
    return undefined;
  }

  return children.slice(0, maxItems).map((child) => ({
    ...child,
    agendas: filterVisibleAgendas(child.agendas, { pinnedOnly }).slice(0, maxItems),
  }));
};

export interface OverviewAgendaItem {
  agendaId: string;
  name: string;
  moduleId: string;
  systemType: string;
  unreadNotificationsCount?: number;
}

// The API's takeAgendas limits Flow agendas only; Helios agendas are never capped.
const limitFlowAgendas = (agendas: Agenda[]): Agenda[] => {
  const limitedFlowAgendaIds = new Set(
    agendas
      .filter((agenda) => agenda.systemType === 'Flow')
      .slice(0, OVERVIEW_FLOW_AGENDA_LIMIT)
      .map((agenda) => agenda.agendaId)
  );

  return agendas.filter((agenda) => agenda.systemType !== 'Flow' || limitedFlowAgendaIds.has(agenda.agendaId));
};

export const getOverviewPinnedModules = (modules: Module[] = []): Module[] =>
  modules
    .filter((module) => module.isPinned)
    .slice(0, OVERVIEW_PINNED_MODULE_LIMIT)
    .map((module) => ({
      ...module,
      agendas: limitFlowAgendas(module.agendas),
      childs: module.childs?.map((child) => ({ ...child, agendas: limitFlowAgendas(child.agendas) })),
    }));

export const getOverviewPinnedAgendaItems = (modules: Module[] = []): OverviewAgendaItem[] =>
  modules.flatMap((module) => [
    ...module.agendas
      .filter((agenda) => agenda.isPinned)
      .map((agenda) => ({
        agendaId: agenda.agendaId,
        name: resolveAgendaName(agenda),
        moduleId: module.moduleId,
        systemType: agenda.systemType,
        unreadNotificationsCount: agenda.unreadNotificationsCount,
      })),
    ...(module.childs ? getOverviewPinnedAgendaItems(module.childs) : []),
  ]);
